import { KeyDict } from '@/types'
import { SceneContent } from '@/types/sceneContent'
import { AssetType } from '@/types/assets'


export class CasterCSS {
  color: string = '#FFFFFF'
  fontFamily?: string = '' // allow fallback when previewing
  fontSize: string = ''
  labelTop?: number = 0
  labelVisible: boolean = true
  moveup: boolean = false
  muted: boolean = false
}

export class ChatUserCSS {
  bg: string = '#000'
  text: string = '#fff'
}

export class ChatCSS {
  general: {
    font: string,
    fontsize: number
  } = {
    font: 'Helvetica',
    fontsize: 6
  }
  user1: ChatUserCSS = new ChatUserCSS()
  user2: ChatUserCSS = new ChatUserCSS()
  user3: ChatUserCSS = new ChatUserCSS()
  user4: ChatUserCSS = new ChatUserCSS()
}

export class TweetCSS {
  avatar: {
    left: number,
    top: number,
    visible: boolean,
    width: number
  } = {
    left: 2,
    top: 13,
    visible: true,
    width: 5
  }
  background: {
    borderradius: number,
    color: string
  } = {
    borderradius: 0,
    color: '#464352'
  }
  handle: {
    color: string,
    font: string,
    fontsize: number,
    left: number,
    top: number,
    visible: boolean,
    width: number
  } = {
    color: 'rgba(255,255,255,1)',
    font: 'Work Sans',
    fontsize: 5,
    left: 80,
    top: 10,
    visible: true,
    width: 3
  }
  question: {
    color: string,
    font: string,
    fontsize: number,
    left: number,
    top: number
  } = {
    color: 'rgba(255,255,255,1)',
    font: 'Work Sans',
    fontsize: 7,
    left: 9,
    top: 13
  }
  tweet: {
    color: string,
    font: string,
    fontsize: number,
    left: number,
    ltr: boolean,
    top: number,
    width: number
  } = {
    color: 'rgba(255,255,255,1)',
    font: 'Work Sans',
    fontsize: 5,
    left: 8,
    top: 40,
    ltr: true,
    width: 80
  }
  username: {
    color: string,
    font: string,
    fontsize: number,
    left: number,
    top: number
  } = {
    color: 'rgba(255,255,255,1)',
    font: 'Work Sans',
    fontsize: 7,
    left: 9,
    top: 13
  }
}

export class SimplecgCSS {
  background: {
    borderradius: number,
    color: string
  } = {
    borderradius: 3,
    color: '#818181'
  }
  text: {
    color: string,
    font: string,
    fontsize: number,
    left: number,
    ltr: boolean,
    top: number,
    width: number
  } = {
    color: 'rgba(255,255,255,1)',
    font: 'Noto Sans',
    fontsize: 16,
    left: 10,
    top: 40,
    ltr: true,
    width: 80
  }
}


export enum BoxGroupType {
  Front = 'front',
  Caster = 'caster',
  Back = 'back'
}

export enum DemoSize {
  Small = 'small',
  Large = 'large',
  Poll = 'poll'
}


export class LiveChatCSS {
  background: {
    borderradius?: number,
    color?: string,
  } = {}
  username: {
    color?: string,
    left?: number,
    top?: number,
    font?: string,
    fontsize?: number
  } = {}
  text: {
    color?: string,
    font?: string,
    fontsize?: number,
    padding?: number,
    left?: number,
    top?: number,
    visible?: boolean
  } = {}
}


export class InstagramCSS {
  background: {
    borderradius?: number,
    color?: string,
  } = {}
  media: {
    cover?: boolean,
  } = {}
  username: {
    color?: string,
    top?: number,
    font?: string,
    fontsize?: number
  } = {}
  text: {
    color?: string,
    font?: string,
    fontsize?: number,
    padding?: number,
    top?: number,
    visible?: boolean
  } = {}
}

export interface SceneTransition {
  type: string,
  duration?: number,
  actions: SceneTransitionAction[]
}

export interface SceneTransitionAction {
  type: string,
  method: string,
  url: string,
  body: string,
  token: string,
  offsetDelay: number
}

export interface Layout {
  id?: string,
  name: string,
  info: string,
  // Layout contents have same interface as scene contents
  contents?: KeyDict<SceneContent>,
  boxes: KeyDict<LayoutBox>,
  editable?: boolean,
  existingLayout?: Layout,
  transition: SceneTransition|string, // FIXME: string will be removed when database gets updated
  castercss: CasterCSS,
  chatcss: ChatCSS,
  tweetcss: TweetCSS,
  instagramcss: InstagramCSS,
  livechatcss: LiveChatCSS,
  simplecgcss: KeyDict<SimplecgCSS>, //Stores individual box stylings, unlike the others
  clips: {
    automute: boolean
  },
  team_id: string
}

export interface LayoutBoxMap {
  [boxid: string]: LayoutBox
}

export enum LayoutBoxField {
  FRONT = 'front',
  BACK = 'back',
  CASTER = 'caster',
  TRANSITION = 'transition'
}

export enum LayoutBoxType {
  BOX = 'box',
  CONTAINER = 'container'
}

export enum ContentAlignment {
  START = 'flex-start',
  CENTER = 'center',
  END = 'flex-end'
}

export enum TransitionType {
  NONE = 'none',
  TRANSLATE = 'translate',
  VIDEO = 'video'
}

export enum HttpRequestMethod {
  GET = 'get',
  PUT = 'put',
  POST = 'post',
  DELETE = 'delete'
}

export interface LayoutBox {
  id: string,
  left: number,
  top: number,
  cascade?: boolean,
  width: number,
  height: number,
  contentalign?: ContentAlignment,
  stretchContent?: boolean,
  stretchVertical?: boolean,
  name: string,
  field?: LayoutBoxField,
  active?: boolean,
  type: LayoutBoxType,
  invisible?: boolean,
  aspectRatio?: boolean,
  zindex: number,
  status?: boolean,
  parent?: string,
  boxes?: KeyDict<LayoutBox>,
  types?: AssetType[],
  // DEPRECATED (?)
  'horizontal-align'?: string,
  'vertical-align'?: string,
}

export enum LayoutBoxOptionType {
  BOOLEAN = 'boolean',
  COLOR = 'color',
  FONT = 'font',
  NUMBER = 'number',
}

export interface LayoutBoxOption {
  name: string,
  title?: string,
  type: LayoutBoxOptionType,
  value: any,
  min?: number,
  max?: number,
}
